'use client';
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { createClient } from '@/utils/supabase/client';
import { User, Session } from '@supabase/supabase-js';

// Create singleton Supabase client for auth context
const supabase = createClient();

interface UserProfile {
  id: string;
  name?: string;
  email?: string;
  created_at?: string;
  last_login?: string;
  is_admin?: boolean;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  isAdmin: boolean;
  loading: boolean;
  profileLoading: boolean;
  signIn: (email: string, password: string) => Promise<any>;
  signUp: (email: string, password: string, name: string) => Promise<any>;
  signOut: () => Promise<any>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<any>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true); // Start as true

  const handleAuthStateChange = async (event: string, session: Session | null) => {
    // Avoid aggressive state resets on transient events
    if (event === 'SIGNED_OUT' || !session?.user) {
      setUser(null);
      setUserProfile(null);
      setIsAdmin(false);
      setLoading(false);
      setProfileLoading(false);
      return;
    }

    // For SIGNED_IN / INITIAL_SESSION: load profile
    // For TOKEN_REFRESHED / USER_UPDATED: try to refresh profile but keep existing state on failure
    setLoading(true);
    setProfileLoading(true);
    try {
      await loadProfile(session.user);
    } catch {
      // Keep existing profile on transient failures
    } finally {
      setLoading(false);
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    const getInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      console.log('[AuthContext] Initial session:', session ? 'Valid' : 'None');
      await handleAuthStateChange('INITIAL_SESSION', session);
    };

    getInitialSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event: string, session: Session | null) => {
      console.log('[AuthContext] Auth state change:', event, 'Session:', session ? 'Valid' : 'None');
      await handleAuthStateChange(event, session);
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const loadProfile = async (user: User) => {
    try {
      const { data, error, status } = await supabase
        .from('user_profiles')
        .select(`id, name, email, created_at, last_login, is_admin`)
        .eq('id', user.id)
        .maybeSingle();

      if (error && status !== 406) {
        throw error;
      }

      if (data) {
        setUser(user);
        setUserProfile(data);
        setIsAdmin(data.is_admin || false);
        // Update last login time in the background
        supabase.from('user_profiles').update({ last_login: new Date().toISOString() }).eq('id', user.id).then(() => { });
      } else {
        // Fallback to minimal profile derived from session without forcing sign-out
        setUser(user);
        setUserProfile({ id: user.id, email: user.email || undefined, name: user.user_metadata?.name });
        setIsAdmin(false);
      }
    } catch (error) {
      console.error("Critical error loading user profile:", error);
      // Keep existing state; do not sign out automatically on transient errors
    }
  };

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    const result = await supabase.auth.signInWithPassword({ email, password });
    // The onAuthStateChange listener will handle the profile loading
    if (result.error) setLoading(false);
    return result;
  };

  const signUp = async (email: string, password: string, name: string) => {
    setLoading(true);
    const result = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name },
        emailRedirectTo: typeof window !== 'undefined' ? `${window.location.origin}/account` : undefined,
      },
    });
    // The onAuthStateChange listener will create the profile on first login
    if (result.error) setLoading(false);
    return result;
  };

  const signOut = async () => {
    setLoading(true);
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error);
      setLoading(false);
    }
    // onAuthStateChange will clear the state
    return { error };
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return { error: { message: 'No user logged in' } };

    setProfileLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id)
        .select('id, name, email, created_at, last_login, is_admin')
        .single();

      if (error) throw error;

      if (data) {
        setUserProfile(data);
        setIsAdmin(data.is_admin || false);
      }
      return { data, error: null };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { data: null, error };
    } finally {
      setProfileLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    userProfile,
    isAdmin,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    isAuthenticated: !!user && !!userProfile, // Stricter check
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
